/**
 * The teleprompter's arithmetic, apart from the screen so it can be tested.
 *
 * A prompter scrolls the script past a reading line at a pace set in words a
 * minute. The pace is the creator's — a slow read is a choice — and the scroll
 * follows the pace, never the other way round.
 */

/** Words in a script, the way a reader counts them. */
export function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

/** How long the script takes to read at a pace, in seconds. */
export function readSeconds(text: string, wordsPerMinute: number): number {
  const wpm = Math.max(40, Math.min(300, wordsPerMinute));
  return (wordCount(text) / wpm) * 60;
}

/**
 * Where the script should be after `elapsed` seconds: pixels scrolled from
 * its resting position, so that the LAST line reaches the reading line when
 * the read ends. `scrollHeight` is the script's full height, `viewport` the
 * prompter's, and the reading line sits `lineAt` (0..1) down the viewport.
 */
export function scrollFor(elapsed: number, text: string, wordsPerMinute: number,
                          scrollHeight: number, viewport: number, lineAt = 0.4): number {
  const total = readSeconds(text, wordsPerMinute);
  if (total <= 0 || scrollHeight <= 0) return 0;
  const travel = Math.max(0, scrollHeight - viewport * lineAt);
  const p = Math.max(0, Math.min(1, elapsed / total));
  return Math.round(travel * p);
}

/** The recorder's container and extension for this browser. */
export function takeFormat(supported: (mime: string) => boolean): { mime: string; ext: string } {
  for (const mime of ['video/mp4;codecs=avc1,mp4a.40.2', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']) {
    if (supported(mime)) return { mime, ext: mime.startsWith('video/mp4') ? 'mp4' : 'webm' };
  }
  return { mime: '', ext: 'webm' };
}

/** A take's name: the scene's first words, the take number, the time. */
export function takeName(script: string, n: number): string {
  const words = script.split(/\s+/).filter(Boolean).slice(0, 5).join(' ');
  return `Take ${n}${words ? ` — ${words}${wordCount(script) > 5 ? '…' : ''}` : ''}`;
}

/** F19: `**word**` marks the key word for the captions; a reader gets the words alone. */
export function unmark(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, '$1');
}
